import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import axios from 'axios';
import { DropdownOption, mapToDropdownOptionArrayWithTextGetter } from 'components/final-form/dropdownUtils';
import Input, { StyledInput } from 'components/final-form/Input';
import FormModalContentContainer from 'components/layout/FormModalContentContainer';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';
import VirtualizedTable, { ExtendedColumnProps } from 'components/layout/VirtualizedTable';
import LoaderComponent from 'components/LoaderComponent';
import TestResultActionButton from 'components/patient-entry/bookings-table/TestResultActionButton';
import moment from 'moment';
import React, { ChangeEvent, useEffect, useState } from 'react';
import { Field, Form as FinalForm } from 'react-final-form';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Button, Icon, InputOnChangeData, Modal } from 'semantic-ui-react';
import { searchGuarantorsForInput } from 'services/guarantorServices';
import { getDetailedPoolTestKitsForSingulation } from 'services/interventionBookingServices';
import { fastPrintPatientDocuments } from 'services/patientServices';
import { singularizePoolTest } from 'services/poolTestkitServices';
import styled from 'styled-components';
import { DetailedPoolTestKitOverviewDto, GuarantorDto, PoolTestkitOverviewResponseDto } from 'ts-types/api.types';
import { isValidBarcode } from 'utils/barcodeUtils';
import { ChromeExtensionStatus, getChromeExtensionStatus, getPrinters, printDocument } from 'utils/chomeExtensionUtils';
import { errorUtils } from 'utils/errorUtils';
import { noop } from 'utils/functionUtils';

const TableDiv = styled.div`
  flex: 1 1 auto;
  min-height: 300px;
`;

const InfoDiv = styled.div`
  & .info-message {
    margin-top: 2rem;
    min-height: 105px;
  }

  & .info-message .download-link {
    margin-top: 1.5rem;
  }

  & .actions .button:first-child {
    margin-left: 0;
  }
`;

const StyledHeader = styled.h4`
  & .pool {
    color: #4183c4;
  }
`;

const SearchSpan = styled.span`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  margin-bottom: 1rem;

  label {
    margin-right: 0.75rem;
    line-height: 2rem;
  }

  .search-input {
    min-width: 18rem;
  }
`;

const StyledModal = styled(Modal)`

  &.ui.modal.active {
    width: 90%;
    height: 90%;
    display: flex !important;
    flex-direction: column;
  }

  &.ui.modal .content {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;

    .edited-cell {
      display: flex;

      .fluid {
        flex: 1 1 auto;
      }
    }

    .testkitcode-container {
      display: flex;
      flex-direction: column;

      .testkitcode-row {
        flex: 1 1 auto;
      }

      .testkitcode-row.original {
        color: #858585;
      }
    }

    .testkitcode-changed {
      color: #1a69a4;
    }

    .cell-actions {
      display: inline-block;
      margin-left: 0.5rem;
    }

    .cell-action {
      margin-right: 0.2rem;
    }

    .no-value {
      color: #959595;
    }
  }
`;

interface EditedTestKit {
  interventionBookingId: number;
  edited: boolean;
  editTestKitCode: string;
  testKitCode?: string;
}

export interface PoolTestkitOverviewProps extends MessageSourceProps, AuthConsumerRenderProps {
  poolTestId: number;
  close: () => void;
}

const PoolTestkitSingularization = (props: PoolTestkitOverviewProps) => {

  const {
    poolTestId,
    getMessage
  } = props;

  const [cancelTokenSource/*, setCancelTokenSource*/] = useState(axios.CancelToken.source());
  const [updatePatientData, setUpdatePatientData] = useState({} as DetailedPoolTestKitOverviewDto | undefined);
  const [editMode, setEditMode] = useState(false);
  const [singularizationInProgress, setSingularizationInProgress] = useState(false);

  const [testkitsLoaded, setTestkitsLoaded] = useState(false);
  const [testkitCode, setTestkitCode] = useState("");

  const [poolTestkitOverviewData, setPoolTestkitOverviewData] =
      useState<PoolTestkitOverviewResponseDto>();

  const [poolTestkits, setPoolTestkits] =
      useState<DetailedPoolTestKitOverviewDto[]>([]);

  const [changedBookingsMap, setChangedBookingsMap] =
      useState<{ [key: number]: EditedTestKit }>({});

  const [printLabelPopupOpen, setPrintLabelPopupOpen] = useState(false);
  const [printLabelPopupStatus, setPrintLabelPopupStatus] = useState("none");

  const [invalidBookings, setInvalidBookings] = useState([] as Array<number>);
  const [errorMessage, setErrorMessage] = useState(undefined as string | undefined);

  useEffect(() => {
    getDetailedPoolTestKitsForSingulation(poolTestId, cancelTokenSource)
        .then(response => {
          setPoolTestkitOverviewData(response);
          setPoolTestkits(response.poolTestkitOverview)
          setTestkitsLoaded(true);
        });
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    setTestkitsLoaded(false);
    setTimeout(() => {
      if (poolTestkitOverviewData) {
        let poolTestkitData = [...poolTestkitOverviewData.poolTestkitOverview];
        poolTestkitData = poolTestkitData.filter(p => {
          const normalizedTestkitCode = p.testKitCode.replace(/[-. ]/g, '');
          const normalizedSearchTerm = testkitCode.toUpperCase().replace(/[-. ]/g, '');
          return normalizedTestkitCode.includes(normalizedSearchTerm);
        })
        setPoolTestkits(poolTestkitData);
        setTestkitsLoaded(true);
      }
    }, 300)
  }, [testkitCode]);

  const handleError = (error: any) => {

    const errorCode = error ? error.code : undefined;
    const knownErrors: Array<string> = [
      errorUtils.invalidBarcode,
      errorUtils.barcodeAlreadyExists,
      errorUtils.poolAlreadySingularized,
      errorUtils.identicalTestkitCodeOnSingularization
    ];

    const violations: Array<any> = error ? error.violations : undefined;

    if (violations && violations.length > 0) {
      const invalidBookingsNew: number[] = []
      violations.forEach((violation: any) => {
        if (violation.field) {
          invalidBookingsNew.push(parseInt(violation.field));
        }
        if (knownErrors.includes(violation.errorCode)) {
          setErrorMessage(getMessage(`error.${violation.errorCode}`))
        }
      });
      setInvalidBookings(invalidBookingsNew);
    } else {
      if (knownErrors.includes(errorCode)) {
        setErrorMessage(getMessage(`error.${errorCode}`))
      } else {
        setErrorMessage(getMessage('error.general'))
      }
    }
  };

  const searchGuarantors = (search: string): Promise<Array<GuarantorDto>> => {
    return searchGuarantorsForInput(search, cancelTokenSource, 75);
  };

  const guarantorMapper = (response: Array<GuarantorDto>): Array<DropdownOption> => {
    return mapToDropdownOptionArrayWithTextGetter(response,
        (guarantor: GuarantorDto) => {
          if (!guarantor.description) {
            return guarantor.bagNumber;
          }
          if (!guarantor.bagNumber) {
            return guarantor.description;
          }
          return `${guarantor.description} - ${guarantor.bagNumber}`;
        });
  };

  const editPatientData = (testId: number) => {

    const poolTestKit = poolTestkitOverviewData!.poolTestkitOverview.find((pt) => {
      return pt.testId == testId
    });

    setUpdatePatientData(poolTestKit);
    setEditMode(true);
  };

  const onCancelEditPatientData = () => {
    setUpdatePatientData(undefined);
    setEditMode(false);
  };

  const renderPoolTesKitTable = (): JSX.Element => {

    const {getMessage} = props;

    const tableColumns: ExtendedColumnProps[] = [
      {
        width: 120,
        label: getMessage("patientCentral.overview.tableHeader.patientId"),
        dataKey: "patientId",
        cellRenderer: patientIdCellRenderer
      },
      {
        width: 265,
        label: getMessage("patientCentral.overview.tableHeader.patientFullName"),
        dataKey: "employeeLastName",
        cellRenderer: employeeFullNameCellRenderer
      },
      {
        width: 200,
        label: getMessage("patientCentral.overview.tableHeader.patientBirthDate"),
        dataKey: "employeeBirthDate",
        cellRenderer: employeeBirthDateCellRenderer
      },
      {
        width: 230,
        label: getMessage("patientCentral.overview.tableHeader.ahvNumber"),
        dataKey: "ahvNumber",
        cellRenderer: ahvNumberCellRenderer
      },
      {
        width: 310,
        label: getMessage("patientCentral.overview.tableHeader.company"),
        dataKey: "companyDescription"
      },
      {
        width: 195,
        label: getMessage("patientCentral.overview.tableHeader.testKitCodeOriginal"),
        dataKey: "testKitCode",
        flexGrow: 0,
        flexShrink: 0,
        cellRenderer: originalTestKitCodeCellRenderer
      },
      {
        width: 255,
        label: getMessage("patientCentral.overview.tableHeader.testKitCodeNew"),
        dataKey: "testKitCode",
        flexGrow: 0,
        flexShrink: 0,
        cellRenderer: testKitCodeCellRenderer
      },
      {
        width: 50,
        flexGrow: 0,
        flexShrink: 0,
        label: (getMessage("actions")),
        dataKey: "testId",
        cellRenderer: actionsCellRenderer
      }
    ];

    return (
        <VirtualizedTable
            rowCount={poolTestkits.length}
            rowGetter={testKitsRowGetter}
            rowRenderer={testKitsRowRenderer}
            rowHeight={57}
            columns={tableColumns}
        />
    )
  };


  const testKitsRowGetter = ({index}: any) => {

    Object.assign(poolTestkits[index], {index: index + 1});

    return poolTestkits[index];
  };

  const testKitsRowRenderer = ({className, columns, index, key, style}: any) => {
    const a11yProps = {'aria-rowindex': index + 1};

    const poolTestKit = poolTestkits[index];

    if (editMode && updatePatientData && updatePatientData.testId == poolTestKit.testId) {

      let styleCopy = style;
      styleCopy.overflow = "visible";

      style = {
        position: "relative"
      };

      return (
          <div style={style}
               key={key}
          >
            <FinalForm
                onSubmit={noop}
                initialValues={updatePatientData}
                subscription={{pristine: true}}
                render={({handleSubmit}) => (
                    <form onSubmit={handleSubmit}
                          {...a11yProps}
                          className={className}
                          role="row"
                          style={styleCopy}
                    >

                      {columns}
                    </form>
                )}
            />
          </div>
      );
    }

    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role="row"
            style={style}
        >
          {columns}
        </div>
    );
  };

  const patientIdCellRenderer = ({rowData}: any) => {
    const isInvalid = invalidBookings.includes(rowData.interventionBookingId);
    return <>
      {rowData.patientId}
      {
          isInvalid &&
          <span>
          <Icon color={'red'} name={'warning sign'}/>
        </span>
      }
    </>;
  };

  const employeeFullNameCellRenderer = ({rowData}: any) => {
    return `${rowData.employeeFirstName} ${rowData.employeeLastName}`;
  };

  const employeeBirthDateCellRenderer = ({rowData}: any) => {
    const now = moment();
    const birthYear = moment(rowData.employeeBirthDate, 'YYYY');

    // calculates employee's age in years
    const employeeAge = now.diff(birthYear, 'years');

    return `${moment(rowData.employeeBirthDate).format("DD.MM.YYYY")} (${employeeAge})`;
  };

  const ahvNumberCellRenderer = ({rowData, cellData}: any) => {
    if (editMode && updatePatientData && updatePatientData.testId == rowData.testId) {
      return (
          <React.Fragment>
            <Field
                name="ahvNumber"
                component={Input}
                fluid
            />
          </React.Fragment>
      );
    }

    return <div>
      <div title={rowData.ahvNumber}>{rowData.ahvNumber}</div>
      <div title={rowData.insuranceDescription}>{rowData.insuranceDescription}</div>
    </div>;
  };

  const addChangedBooking = (test: DetailedPoolTestKitOverviewDto) => {
    const newMap = {
      ...changedBookingsMap
    };
    newMap[test.interventionBookingId] = {
      interventionBookingId: test.interventionBookingId,
      edited: true,
      editTestKitCode: ""
    };
    setChangedBookingsMap(newMap);
  }

  const editChangedBooking = (test: EditedTestKit) => {
    const newMap = {
      ...changedBookingsMap
    };
    newMap[test.interventionBookingId] = test;
    setChangedBookingsMap(newMap);
  }

  const testKitCodeCellRenderer = ({rowData, cellData}: any): JSX.Element => {

    const test = rowData as DetailedPoolTestKitOverviewDto;

    if (test.singularziedTestKitCode) {
      return <span className="testkitcode-changed"
                   title={test.singularziedTestKitCode}
      >
        {test.singularziedTestKitCode}
      </span>
    }

    const extendedBookingData = changedBookingsMap[test.interventionBookingId];
    if (extendedBookingData && extendedBookingData.edited) {
      const isValid = isValidBarcode(extendedBookingData.editTestKitCode);
      return (
          <div className="edited-cell">
            <StyledInput
                className={isValid ? "" : "error"}
                placeholder=""
                fluid
                value={extendedBookingData.editTestKitCode}
                onChange={(evt: ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => {
                  editChangedBooking({
                    ...extendedBookingData,
                    editTestKitCode: data.value
                  });
                }}
            />
            <span className="cell-actions">
              <Icon name="check"
                    color="green"
                    className="cell-action"
                    disabled={!isValid}
                    onClick={() => {
                      if (extendedBookingData.editTestKitCode !== test.testKitCode) {
                        editChangedBooking({
                          ...extendedBookingData,
                          testKitCode: extendedBookingData.editTestKitCode,
                          edited: false
                        });
                      }
                    }}
              />
            <Icon name="delete"
                  className="cell-action"
                  color="red"
                  onClick={() => {
                    editChangedBooking({
                      ...extendedBookingData,
                      edited: false
                    })
                  }}
            />
            </span>
          </div>
      );
    } else if (extendedBookingData && extendedBookingData.testKitCode) {
      const testKitCodeValue = extendedBookingData.testKitCode;
      const testKitCodeChanged = extendedBookingData.testKitCode
          && test.testKitCode !== extendedBookingData.testKitCode;
      return <>
        <span className={testKitCodeChanged ? "testkitcode-changed" : ""}
              title={testKitCodeValue}
        >
          {testKitCodeValue}
        </span>
        <span className="cell-actions">
          <Icon name="edit"
                className="cell-action"
                onClick={() => {
                  editChangedBooking({
                    ...extendedBookingData,
                    editTestKitCode: testKitCodeValue || "",
                    edited: true
                  })
                }}
          />
          {
              testKitCodeChanged &&
              <Icon name="eraser"
                    className="cell-action"
                    onClick={() => {
                      editChangedBooking({
                        ...extendedBookingData,
                        testKitCode: undefined
                      })
                    }}
              />
          }
        </span>
      </>
    } else {
      return <>
        <span className="no-value">n/a</span>
        <span className="cell-actions">
          <Icon name="edit"
                className="cell-action"
                onClick={() => addChangedBooking(test)}
          />
        </span>
      </>
    }
  };

  const originalTestKitCodeCellRenderer = ({rowData}: any) => {
    const test = rowData as DetailedPoolTestKitOverviewDto;
    return test.testKitCode;
  };

  const actionsCellRenderer = ({rowData, cellData}: any) => {
    const {getMessage} = props;

    // if (editMode && updatePatientData && updatePatientData.testId == rowData.testId) {
    //   return (
    //       <React.Fragment>
    //         <SaveActionButton hoverMessage={getMessage('button.save')}/>
    //         <CancelActionButton hoverMessage={getMessage('button.cancel')} onConfirm={onCancelEditPatientData}/>
    //       </React.Fragment>
    //   );
    // }

    return (
        <React.Fragment>
          {/*<EditActionButton*/}
          {/*    hoverMessage={getMessage('msgEdit')}*/}
          {/*    onConfirm={() => editPatientData(cellData)}*/}
          {/*    // disabled={editMode}*/}
          {/*/>*/}

          <TestResultActionButton
              poperDependencies={[printLabelPopupOpen]}
              renderContent={(toggle?: () => void) => printBarcodeLabelPopupContent(toggle)}
              hoverMessage={getMessage("patient.toolbar.praxMedicNativeTools")}
              onConfirm={() => {
              }}
              onOpen={() => triggerPrintpatientLabels(rowData.patientId)}
              trigger={<Icon name={'print'}/>}
          />
        </React.Fragment>
    );
  };

  const printBarcodeLabelPopupContent = (toggle: (() => void) | undefined) => {
    const {getMessage} = props;

    let content = () => {
      switch (printLabelPopupStatus.toUpperCase()) {
        case 'STARTED' :
          return <div>...</div>;

        case 'NO_EXTENSION' :
          return <InfoDiv>
            <div className="info-message">
              {getMessage('nativeTools.status.NO_EXTENSION')}
              <div className="download-link">
                <a href="https://chrome.google.com/webstore/detail/iggeppehjnmjeeicldkpjopdofglljei?authuser=0&hl=en"
                   target="_blank">{getMessage('nativeTools.download.chromeExtension')}</a>
              </div>
            </div>
          </InfoDiv>;

        case 'OUTDATED_EXTENSION' :
          return <InfoDiv>
            <div className="info-message">
              {getMessage('nativeTools.status.OUTDATED_EXTENSION')}
              <div className="download-link">
                <a href="https://praxmedic.ch/native-tools" target="_blank">
                  {getMessage('nativeTools.download.chromeExtension')}
                </a>
              </div>
            </div>
          </InfoDiv>;

        case 'NO_HOST_APPLICATION' :
          return <InfoDiv>
            <div className="info-message">
              {getMessage('nativeTools.status.NO_HOST_APPLICATION')}
              <div className="download-link">
                <a href="https://praxmedic.ch/native-tools" target="_blank">
                  {getMessage('nativeTools.download.hostApplication')}
                </a>
              </div>
            </div>
          </InfoDiv>;

        case 'OUTDATED_HOST_APP' :
          return <InfoDiv>
            <div className="info-message">
              {getMessage('nativeTools.status.OUTDATED_HOST_APP')}
              <div className="download-link">
                <a href="https://praxmedic.ch/native-tools" target="_blank">
                  {getMessage('nativeTools.download.hostApplication')}
                </a>
              </div>
            </div>
          </InfoDiv>;

        case 'PRINTER_NOT_FOUND' :
          return <div>The label printer was not found.</div>;

        case 'RETRIEVING_PRINTERS' :
          return <div>Retrieving available printers...</div>;

        case 'CREATING_LABELS' :
          return <div>Creating labels...</div>;

        case 'PRINTING_LABELS' :
          return <div>Printing labels...</div>;

        case 'DONE' :
          return <div>Labels printed.</div>;
      }
    }

    return (
        <div>
          {content()}
          <div>
            <Button type="button" compact onClick={() => closePrintLabelPopup(toggle)}>
              {getMessage("button.close")}
            </Button>
          </div>
        </div>
    )
  }

  const closePrintLabelPopup = (toggle: (() => void) | undefined) => {

    if (toggle) {
      toggle();
    }

    setPrintLabelPopupOpen(false);
  }

  const triggerPrintpatientLabels = async (patientId: number) => {
    const {config} = props;

    setPrintLabelPopupOpen(true);
    setPrintLabelPopupStatus("STATED");

    const extStatus = await getChromeExtensionStatus(config);

    if (ChromeExtensionStatus.NO_EXTENSION === extStatus) {
      setPrintLabelPopupStatus(extStatus);
    } else if (ChromeExtensionStatus.OUTDATED_EXTENSION === extStatus) {
      setPrintLabelPopupStatus(extStatus);
    } else if (ChromeExtensionStatus.NO_HOST_APPLICATION === extStatus) {
      setPrintLabelPopupStatus(extStatus);
    } else if (ChromeExtensionStatus.OUTDATED_HOST_APP === extStatus) {
      setPrintLabelPopupStatus(extStatus);
    } else if (ChromeExtensionStatus.OK === extStatus) {

      setPrintLabelPopupStatus("RETRIEVING_PRINTERS");

      const printersResult: any = await getPrinters();
      const printers = printersResult.payload;
      // @ts-ignore
      const labelPrinter: any = printers.find(printer => {
        const printerName: string = printer.name.toLowerCase();
        return printerName.includes("dymo") && printerName.includes("450");
        // return printerName.includes("microsoft") && printerName.includes("pdf");
      });

      if (labelPrinter) {
        setPrintLabelPopupStatus("CREATING_LABELS");

        const documents = await fastPrintPatientDocuments(cancelTokenSource, patientId);

        setPrintLabelPopupStatus("PRINTING_LABELS");

        let i = 0;
        for (const doc of documents) {
          // print only 2 labels here, instead of 3 as everywhere else
          if (i >= 2) {
            break;
          }
          await printDocument(doc, labelPrinter.name);
          i++;
        }

        setPrintLabelPopupStatus("DONE");
      } else {
        setPrintLabelPopupStatus("PRINTER_NOT_FOUND");
      }
    }
  }

  const renderModalContent = () => {
    if (poolTestkitOverviewData) {

      const {poolTestkitOverview, poolTestkitCode, laboratoryDisplayName} = poolTestkitOverviewData;

      const countPooleTestkits = poolTestkitOverview.length;

      return (
          <>
            <StyledHeader>
              {getMessage("patientCentral.overview.row.pooltestKit")}
              &nbsp; (<span>#{countPooleTestkits}</span>)
              &nbsp; - &nbsp;
              <span>{poolTestkitCode}</span>
              {(laboratoryDisplayName !== null && laboratoryDisplayName !== undefined && laboratoryDisplayName.length > 0)
                  &&
                  <span className="">&nbsp; : &nbsp; {laboratoryDisplayName}</span>
              }
            </StyledHeader>
            {
                errorMessage &&
                <StyledErrorMessage onDismiss={() => setErrorMessage(undefined)}>
                  {errorMessage}
                </StyledErrorMessage>
            }
            <SearchSpan>
              <label htmlFor={"search"}>{getMessage("search")}: </label>
              <StyledInput
                  id={"search"}
                  className={"search-input"}
                  value={testkitCode}
                  onChange={(e: any) => setTestkitCode(e.target.value)}
                  fluid
              />
            </SearchSpan>
            <TableDiv>
              {testkitsLoaded
                  ? renderPoolTesKitTable()
                  : <LoaderComponent message={getMessage("loading")}/>
              }
            </TableDiv>
          </>
      );
    } else {
      return (
          <LoaderComponent message={getMessage("loading")}/>
      );
    }
  }

  const singularizationPool = async () => {
    setErrorMessage(undefined);

    const {currentUser} = props;
    const mandateId = currentUser!.userConfig.mandateId;
    const changedBookingIds = Object.keys(changedBookingsMap);
    const bookingIds = poolTestkitOverviewData!.poolTestkitOverview.map(p => p.interventionBookingId);

    let bookingErrors: number[] = [];
    bookingIds.forEach(bookingId => {
      if (!changedBookingIds.includes(bookingId.toString())) {
        bookingErrors.push(bookingId);
      }
    })
    setInvalidBookings(bookingErrors);

    if (bookingErrors.length > 0) {
      return;
    }

    setSingularizationInProgress(true);

    const changedTestKitCodes: { [key: number]: string } = {};
    Object.values(changedBookingsMap).forEach(test => {
      if (test.testKitCode) {
        changedTestKitCodes[test.interventionBookingId] = test.testKitCode;
      }
    });

    try {
      await singularizePoolTest(props.poolTestId, changedTestKitCodes, mandateId, cancelTokenSource);
    } catch (e) {
      handleError(e.response.data);
      setTestkitCode("");
      return;
    } finally {
      setSingularizationInProgress(false);
    }

    props.close();
  };

  const poolSingularized = poolTestkitOverviewData && poolTestkitOverviewData.poolSingularized;

  return <StyledModal
      open={true}
      onClose={props.close}
      closeOnDimmerClick={false}
      size="large"
  >
    <Modal.Header>
      {getMessage("patientCentral.pool-singularization.title")}
    </Modal.Header>
    <FormModalContentContainer>
      {renderModalContent()}
    </FormModalContentContainer>
    <Modal.Actions>
      <Button type="button"
              compact
              floated="left"
              primary
              disabled={singularizationInProgress || poolSingularized}
              onClick={singularizationPool}
      >
        {getMessage("patientCentral.pool-singularization.action.singularize")}
      </Button>

      <Button compact type="button" onClick={props.close}>
        {getMessage("button.close")}
      </Button>
    </Modal.Actions>
  </StyledModal>
};

export default withMessages(withAuthContext(PoolTestkitSingularization));
